import { RegimeArtwork } from "../components/regulation/RegimeArtwork";
/**
 * A regime's own page: every control it carries, and whether this firm has
 * answered it — a control counts as addressed when a document is linked to
 * it or a person marked its status satisfied / not applicable. `regimes_`
 * (trailing underscore) keeps it a sibling of the catalogue route, which
 * has no Outlet.
 *
 * @module routes/$org.compliance.regimes_.$regimeId
 */
import { Link, createFileRoute, useLocation } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckCircle2Icon,
  ChevronRightIcon,
  CircleIcon,
  FileTextIcon,
  MinusCircleIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "~/lib/utils";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../components/ui/collapsible";
import { RegimeDetailTabs } from "../components/regulation/RegimeDetailTabs";
import { RegimePublicationsSection } from "../components/regulation/RegimePublicationsSection";
import { ListSkeleton } from "../components/regulation/LoadingSkeletons";
import { RegulationPageShell } from "../components/regulation/RegulationPageShell";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { useFrameworks, useGovernanceFrameworks } from "../state/governance";

const CARD = "rounded-2xl border border-border/60 bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)]";

interface ControlRow {
  readonly id: string;
  readonly code: string;
  readonly title: string;
  readonly description: string | null;
  readonly category: string | null;
  readonly status: string;
  readonly policies: ReadonlyArray<{
    readonly id: string;
    readonly filename: string;
    readonly title: string | null;
  }>;
}

/** Addressed = a document answers it, or a person marked it so. */
const isAddressed = (control: ControlRow): boolean =>
  control.policies.length > 0 ||
  control.status === "satisfied" ||
  control.status === "not_applicable";

function ControlItem({ control, orgSegment }: { readonly control: ControlRow; readonly orgSegment: string }) {
  const addressed = isAddressed(control);
  const byStatusOnly = addressed && control.policies.length === 0;
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      {addressed ? (
        control.status === "not_applicable" && control.policies.length === 0 ? (
          <MinusCircleIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        ) : (
          <CheckCircle2Icon className="mt-0.5 size-4 shrink-0 text-success" />
        )
      ) : (
        <CircleIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground/40" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground">
          <span className="me-2 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
            {control.code}
          </span>
          <span className={cn("font-medium", !addressed && "text-foreground/90")}>{control.title}</span>
        </p>
        {control.description ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{control.description}</p>
        ) : null}
        {control.policies.length > 0 ? (
          <span className="mt-1.5 flex flex-wrap gap-1.5">
            {control.policies.map((policy) => (
              <Link
                key={policy.id}
                to="/$org/compliance/policies/$policyId"
                params={{ org: orgSegment, policyId: policy.id }}
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-border/60 bg-card px-2 py-0.5 text-[11px] text-foreground hover:border-foreground/40"
              >
                <FileTextIcon className="size-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{policy.title ?? policy.filename}</span>
              </Link>
            ))}
          </span>
        ) : byStatusOnly ? (
          <p className="mt-1 text-[11px] text-muted-foreground">
            Marked {control.status === "not_applicable" ? "not applicable" : "satisfied"} — no document linked.
          </p>
        ) : null}
      </div>
    </li>
  );
}

function RegulationRegimeRoute() {
  const { org: orgSegment, regimeId } = Route.useParams();
  const navigate = Route.useNavigate();
  const hash = useLocation({ select: (location) => location.hash });
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const { snapshot } = useFrameworks(organizationId);
  const governance = useGovernanceFrameworks(organizationId);
  const [controls, setControls] = useState<ReadonlyArray<ControlRow> | null>(null);
  const regime = snapshot?.frameworks.find((item) => item.id === regimeId) ?? null;
  const answeringFrameworks = (governance.snapshot?.frameworks ?? []).filter((framework) =>
    framework.regimes.some((item) => item.id === regimeId),
  );

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    setControls(null);
    void (async () => {
      try {
        const response = await fetch(
          resolvePrimaryEnvironmentHttpUrl("/api/amoreg/regulation/controls", {
            organizationId,
            frameworkId: regimeId,
          }),
          { credentials: "include" },
        );
        const json = response.ok
          ? ((await response.json()) as { controls?: ControlRow[] })
          : { controls: [] };
        if (!cancelled) setControls(json.controls ?? []);
      } catch {
        if (!cancelled) setControls([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, regimeId]);

  const back = (
    <Link
      to="/$org/compliance/regimes"
      params={{ org: orgSegment }}
      className="inline-flex w-fit cursor-pointer items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
    >
      <ArrowLeftIcon className="size-3.5" />
      All regimes
    </Link>
  );

  if (regime === null) {
    return (
      <RegulationPageShell eyebrow={back} title="Regime">
        {() =>
          isLoading || snapshot === null ? (
            <div className="mt-6">
              <ListSkeleton rows={4} />
            </div>
          ) : (
            <div className="mt-6 rounded-xl border border-dashed border-border/60 px-6 py-12 text-center">
              <ShieldCheckIcon className="mx-auto size-5 text-muted-foreground/60" />
              <p className="mt-3 text-sm font-medium text-foreground">Regime not found</p>
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                It is not in the catalogue {org?.name ?? "your organization"} sees.
              </p>
            </div>
          )
        }
      </RegulationPageShell>
    );
  }

  const addressed = (controls ?? []).filter(isAddressed).length;
  const grouped = new Map<string, ControlRow[]>();
  for (const control of controls ?? []) {
    const key = control.category ?? "General";
    grouped.set(key, [...(grouped.get(key) ?? []), control]);
  }

  const adoption = (
    <span className={cn(
      "rounded-full px-2 py-0.5 text-xs font-medium",
      regime.enabled ? "bg-success/10 text-success" : "bg-muted text-muted-foreground",
    )}>
      {regime.enabled ? "Adopted" : "Not adopted"}
    </span>
  );
  const addressedExplanation = "A control is addressed when a document is linked to it (a policy's Controls tab → Link controls) or a person marks it satisfied or not applicable.";

  return (
    <RegulationPageShell
      eyebrow={
        <div className="flex items-center justify-between gap-3">
          {back}
          <span className="sm:hidden">{adoption}</span>
        </div>
      }
      title={regime.name}
      artwork={<RegimeArtwork regimeId={regime.id} prominent />}
      titleAside={<span className="hidden sm:inline-flex">{adoption}</span>}
      description={
        <span className="text-xs leading-relaxed sm:text-sm">
          {[regime.jurisdiction, regime.issuer, regime.version].filter(Boolean).join(" · ")}
        </span>
      }
      meta={
        controls !== null && controls.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:mt-4">
            <div className="order-2 h-1 w-16 shrink-0 overflow-hidden rounded-full bg-muted sm:order-none sm:h-1.5 sm:w-40">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${Math.round((addressed / controls.length) * 100)}%` }}
              />
            </div>
            <span className="order-1 text-xs tabular-nums text-muted-foreground sm:order-none">
              {addressed} of {controls.length} controls addressed
            </span>
            {answeringFrameworks.length > 0 ? (
              <span className="order-3 w-full text-xs leading-relaxed text-muted-foreground sm:order-none sm:w-auto">
                <span className="hidden sm:inline">· </span>Answered by{" "}
                {answeringFrameworks.map((framework, index) => (
                  <span key={framework.id}>
                    {index > 0 ? ", " : ""}
                    <Link
                      to="/$org/compliance/frameworks/$frameworkId"
                      params={{ org: orgSegment, frameworkId: framework.id }}
                      className="text-foreground hover:underline"
                    >
                      {framework.name}
                    </Link>
                  </span>
                ))}
              </span>
            ) : null}
          </div>
        ) : undefined
      }
    >
      {() => (
        <RegimeDetailTabs
          hash={hash}
          onTabChange={(tab) =>
            void navigate({ hash: tab, resetScroll: false, hashScrollIntoView: false })
          }
          controlCount={controls?.length ?? null}
          sources={
            <RegimePublicationsSection organizationId={organizationId} regimeId={regimeId} />
          }
          controls={controls === null ? (
            <ListSkeleton rows={5} />
          ) : controls.length === 0 ? (
            <p className="border-t border-border/60 py-5 text-sm text-muted-foreground">
              No controls loaded for this regime yet.
            </p>
          ) : (
            <div className="space-y-5">
              <p className="hidden text-xs leading-relaxed text-muted-foreground sm:block">
                {addressedExplanation}
              </p>
              <Collapsible className="sm:hidden">
                <CollapsibleTrigger className="group flex cursor-pointer items-center gap-1 text-xs text-muted-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
                  <ChevronRightIcon className="size-3.5 transition-transform group-data-[panel-open]:rotate-90 motion-reduce:transition-none" />
                  How controls are addressed
                </CollapsibleTrigger>
                <CollapsibleContent className="motion-reduce:transition-none">
                  <p className="pt-2 text-xs leading-relaxed text-muted-foreground">{addressedExplanation}</p>
                </CollapsibleContent>
              </Collapsible>
              {[...grouped.entries()].map(([category, items]) => (
                <section key={category} className="space-y-2">
                  <h2 className="flex items-baseline gap-2 text-[15px] font-semibold text-foreground">
                    {category}
                    <span className="text-xs font-normal tabular-nums text-muted-foreground">
                      {items.filter(isAddressed).length}/{items.length}
                    </span>
                  </h2>
                  <ul className={cn(CARD, "divide-y divide-border/60 overflow-hidden")}>
                    {items.map((control) => (
                      <ControlItem key={control.id} control={control} orgSegment={orgSegment} />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        />
      )}
    </RegulationPageShell>
  );
}

export const Route = createFileRoute("/$org/compliance/regimes_/$regimeId")({
  component: RegulationRegimeRoute,
});
